import { ReactComponent as OutlineEmptyResult } from "assets/svg/outline-empty-result.svg";
import { ReactComponent as ShieldCross } from "assets/svg/shield-cross.svg";
import AppPagination from "components/app-pagination";
import OrchidProCardMobile from "components/orchid-pro-card-mobile/OrchidProCardMobile";
import { STATE_SEARCH_OPTIONS } from "components/utility/option-constants";
import { useUser } from "contexts/UserContext";
import { usePhonePortraitContext } from "contexts/phone-portrait-context";
import { useTimeOnPageContext } from "contexts/time-on-page";
import useLocationQueryString from "hooks/useLocationQueryString";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { useParams } from "react-router-dom";
import queryPatient from "services/queryPatient";
import searchOrchidPro from "services/searchOrchidPro";
import { AmplitudePageName } from "utils/amplitude";
import getClasses from "utils/getClasses";
import redirectTo404 from "utils/redirectTo404";
import Loading from "../Loading";
import Footer from "../footer/Footer";
import OrchidProCard from "../orchid-pro/OrchidProCard";
import styles from "./MarketPlace.module.scss";
import MarketPlaceBreadcrumbs from "./market-place-breadcrumbs/MarketPlaceBreadcrumbs";
import MarketPlaceFaq from "./market-place-faq/MarketPlaceFaq";
import MarketPlaceHeader from "./market-place-header/MarketPlaceHeader";
import MarketPlaceTestimonials from "./market-place-testimonials/MarketPlaceTestimonials";

const PAGE_SIZE = 10;

const PAGE_QUERY_KEY = "page";

const AMPLITUDE_PAGE_NAME: AmplitudePageName = "professional-search-page";

export type SPECIALTY_VALUE = "Therapist" | "Psychologist" | "Psychiatry" | "";

export const SPECIALTY_OPTIONS: {
  value: SPECIALTY_VALUE;
  label: string;
}[] = [
  { value: "Therapist", label: "Therapist" },
  { value: "Psychologist", label: "Psychologist" },
  { value: "Psychiatry", label: "Psychiatrist" },
];

interface MarketPlaceProps {
  initialSpecialty?: SPECIALTY_VALUE;
}

const MarketPlace = ({ initialSpecialty }: MarketPlaceProps) => {
  const { initialStateUrl } = useParams<{
    initialStateUrl?: string;
  }>();

  const { setPage: timeOnPageSetPage } = useTimeOnPageContext();
  useEffect(() => timeOnPageSetPage(AMPLITUDE_PAGE_NAME), [timeOnPageSetPage]);

  const { user } = useUser();
  const { isPhonePortrait } = usePhonePortraitContext();
  const pageQuery = useLocationQueryString().get(PAGE_QUERY_KEY);

  const [page, setPage] = useState(!pageQuery ? 1 : +pageQuery || 1);
  const [busy, setBusy] = useState(true);

  const [doctorName, setDoctorName] = useState("");
  const [zipcode, setZipcode] = useState("");
  const [state, setState] = useState(
    STATE_SEARCH_OPTIONS.find((option) => option.urlValue === initialStateUrl)
      ?.value ?? "",
  );
  const [specialty, setSpecialty] = useState<SPECIALTY_VALUE>(
    initialSpecialty ?? "",
  );

  const isGenericSeachHomePage = useMemo(
    () => !specialty && !state,
    [specialty, state],
  );

  const [userHomeState, setUserHomeState] = useState("");

  // If an invalid state is provider in the URL, redirect to 404
  useEffect(() => {
    if (!initialStateUrl) {
      return;
    }
    const stateOption = STATE_SEARCH_OPTIONS.find(
      (option) => option.urlValue === initialStateUrl,
    );

    if (!stateOption) {
      redirectTo404();
      return;
    }

    setDoctorName("");
    setState(stateOption.value);
  }, [initialStateUrl]);

  useEffect(() => {
    setDoctorName("");
    setSpecialty(initialSpecialty ?? "");
  }, [initialSpecialty]);

  useEffect(() => {
    if (state) {
      setZipcode("");
    }
  }, [state]);

  // If login user is patient, get patient state on init
  useEffect(() => {
    if (user?.user_type === "member" && !userHomeState) {
      queryPatient().then((res) => {
        const { home_state } = res;
        if (home_state) {
          if (!initialStateUrl) {
            setState(home_state);
          }
          setUserHomeState(home_state);
        }
      });
    }
  }, [user, userHomeState, initialStateUrl]);

  const [{ physicians, typeaheadOptions, totalCount }, setResponse] = useState<{
    physicians: OrchidPro[];
    typeaheadOptions: { id: number; typeahead: string }[];
    totalCount: number;
  }>({
    physicians: [],
    typeaheadOptions: [],
    totalCount: 0,
  });

  const abortControllerRef = useRef<AbortController>();

  const doSearch = useCallback(async () => {
    const controller = abortControllerRef.current;
    if (controller) {
      controller.abort();
    }

    const nextController = new AbortController();
    abortControllerRef.current = nextController;

    try {
      setBusy(true);
      const res = await searchOrchidPro(
        {
          orchid_pro_name: doctorName,
          zipcode,
          state,
          limit: PAGE_SIZE,
          skip: ((page || 1) - 1) * PAGE_SIZE,
          specialty,
        },
        {
          signal: nextController.signal,
        },
      );

      const options = res.typeahead.map((i, index) => ({
        typeahead: i,
        id: index,
      }));
      setResponse({
        physicians: res.results,
        typeaheadOptions: options,
        totalCount: res.total_count,
      });
    } catch (err: any) {
      if (err.name !== "AbortError") {
        console.error(err);
      }
    } finally {
      setBusy(false);
    }
  }, [doctorName, page, state, zipcode, specialty]);

  const handleSearchSubmit = useCallback(
    (ev) => {
      ev.preventDefault();
      doSearch();
    },
    [doSearch],
  );

  useEffect(() => {
    doSearch();
  }, [doSearch]);

  const specialtyName = useMemo(
    () =>
      SPECIALTY_OPTIONS.find((option) => option.value === specialty)?.label ??
      "Clinician",
    [specialty],
  );

  const stateName = useMemo(
    () =>
      STATE_SEARCH_OPTIONS.find((option) => option.value === state)?.label ??
      "",
    [state],
  );

  const pageTitle = useMemo(() => {
    if (isGenericSeachHomePage) {
      return "Look for a professional";
    }

    const specialty = `${
      specialtyName === "Clinician"
        ? "professional"
        : specialtyName.toLowerCase()
    }s`;

    if (!state) {
      return `Book top ${specialty}`;
    }

    if (state === userHomeState) {
      return `Book top ${specialty} near me`;
    }

    return `Book top ${specialty} in ${stateName}`;
  }, [isGenericSeachHomePage, specialtyName, state, stateName, userHomeState]);

  const title = useMemo(
    () =>
      `Find a ${specialtyName} ${stateName ? "in " + stateName : ""}| Orchid`,
    [stateName, specialtyName],
  );

  const metaDescription = useMemo(
    () =>
      `Discover top ${specialtyName.toLowerCase()}s in mental health. Book the best psychiatrist, psychologist, therapist, or counselor ${
        stateName ? "in " + stateName : "near you"
      }. Learn more at orchid.exchange`,
    [stateName, specialtyName],
  );

  const uniqueCopyForSeo = useMemo(() => {
    if (busy || doctorName) {
      return "";
    }

    const locationInfo = stateName ? `in ${stateName} ` : " ";

    if (totalCount === 1) {
      return `There is 1 ${specialtyName.toLowerCase()} ${locationInfo}available on Orchid.`;
    }

    return `There are ${totalCount} ${specialtyName.toLowerCase()}s ${locationInfo}available on Orchid.`;
  }, [busy, totalCount, stateName, specialtyName, doctorName]);

  const targetPathname = useMemo(() => {
    if (!specialty && !state) {
      return "/professional-search";
    }

    const specialtySegment = specialty
      ? `${specialtyName.toLowerCase()}s`
      : "professionals";

    if (!state) {
      return `/${specialtySegment}`;
    }

    const stateSegment =
      STATE_SEARCH_OPTIONS.find((option) => option.value === state)?.urlValue ??
      "";

    return `/${specialtySegment}/${stateSegment}`;
  }, [specialty, state, specialtyName]);

  const targetSearch = useMemo(() => {
    return page > 1 ? `?page=${page}` : "";
  }, [page]);

  useEffect(() => {
    const currentPathAndSearch =
      window.location.pathname + window.location.search;

    const targetPathnameAndSearch = targetPathname + targetSearch;

    if (currentPathAndSearch === targetPathnameAndSearch) {
      return;
    }
    window.history.pushState({}, "", targetPathnameAndSearch);
  }, [targetPathname, targetSearch]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [state, specialty, page]);

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={metaDescription} />

        <meta property="og:title" content={title} />
        <meta property="og:description" content={metaDescription} />
        <meta
          property="og:image"
          content="https://www.orchid.exchange/img/professional-search/landing.png"
        />
        <meta
          property="og:image:url"
          content="https://www.orchid.exchange/img/professional-search/landing.png"
        />
        <meta
          property="og:image:secure_url"
          content="https://www.orchid.exchange/img/professional-search/landing.png"
        />
        <meta property="og:image:alt" content="" />
        <meta property="og:image:width" content="960" />
        <meta property="og:image:height" content="304" />
        <meta property="og:image:type" content="image/png" />

        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={metaDescription} />
        <meta
          name="twitter:image"
          content="https://www.orchid.exchange/img/professional-search/landing.png"
        />
        <meta
          name="twitter:image:src"
          content="https://www.orchid.exchange/img/professional-search/landing.png"
        />
        <meta name="twitter:image:alt" content="" />
      </Helmet>

      <MarketPlaceHeader
        isGenericSeachHomePage={isGenericSeachHomePage}
        pageTitle={pageTitle}
        typeaheadOptions={typeaheadOptions}
        specialty={specialty}
        setSpecialty={setSpecialty}
        state={state}
        setState={setState}
        zipcode={zipcode}
        setZipcode={setZipcode}
        setDoctorName={setDoctorName}
        setPage={setPage}
        handleSearchSubmit={handleSearchSubmit}
        amplitudePageName={AMPLITUDE_PAGE_NAME}
      />

      {busy ? (
        <Loading />
      ) : (
        <>
          {!isGenericSeachHomePage && (
            <>
              <div className={styles["wrapper"]}>
                <div className={styles["container"]}>
                  <MarketPlaceBreadcrumbs
                    specialtyName={specialtyName}
                    stateName={stateName}
                    targetPathname={targetPathname}
                  />
                </div>
              </div>
              <div
                className={getClasses(
                  styles["wrapper"],
                  isPhonePortrait &&
                    styles["wrapper--verified-professionals--mobile"],
                )}
              >
                <div className={styles["container"]}>
                  <div
                    className={getClasses(
                      styles["verified-professionals"],
                      isPhonePortrait &&
                        styles["verified-professionals--mobile"],
                    )}
                  >
                    <div className={styles["verified-professionals__header"]}>
                      <ShieldCross />
                      <h2>
                        Verified {specialtyName.toLowerCase()}s in your area
                      </h2>
                    </div>
                    All clinicians listed in Orchid search results have been
                    verified by Orchid and are open to accepting or waitlisting
                    new clients.
                  </div>
                </div>
              </div>
            </>
          )}

          <div className={styles["wrapper"]}>
            <div
              className={getClasses(
                styles["container"],
                isPhonePortrait && styles["container--mobile"],
              )}
            >
              <div
                className={getClasses(
                  styles["unique-copy"],
                  isPhonePortrait && styles["unique-copy--mobile"],
                )}
              >
                {uniqueCopyForSeo}
              </div>

              {physicians.length === 0 ? (
                <div className={styles["empty-state"]}>
                  <OutlineEmptyResult />
                  <div className={styles["empty-state__line-1"]}>
                    Sorry, no results were found.
                  </div>
                  <div className={styles["empty-state__line-2"]}>
                    Try adjusting your search filters to see available providers
                  </div>
                </div>
              ) : (
                physicians.map((orchid_pro) =>
                  isPhonePortrait ? (
                    <OrchidProCardMobile
                      key={orchid_pro.cognito_sub}
                      orchidPro={orchid_pro}
                      amplitudePageName={AMPLITUDE_PAGE_NAME}
                    />
                  ) : (
                    <OrchidProCard
                      key={orchid_pro.cognito_sub}
                      orchidPro={orchid_pro}
                      highlightToday
                      amplitudePageName={AMPLITUDE_PAGE_NAME}
                    />
                  ),
                )
              )}

              <div className={styles["app-pagination"]}>
                <AppPagination
                  onPageChange={(nextPage) => {
                    setPage(nextPage);
                  }}
                  page={page}
                  dataCount={totalCount}
                  dataCountPerPage={PAGE_SIZE}
                />
              </div>
            </div>
          </div>
        </>
      )}

      <MarketPlaceTestimonials specialtyValue={specialty} />

      <MarketPlaceFaq specialtyLabel={specialtyName.toLowerCase()} />

      <Footer AMPLITUDE_PAGE_NAME={AMPLITUDE_PAGE_NAME} />
    </>
  );
};

export default MarketPlace;
